import html2pdf from "html2pdf.js";
import { formatAccountantWhatsAppMessage } from "./gstExportUtils.js";

/**
 * Common html2pdf options for high-resolution A4 GST Audit Report
 */
function getPdfOptions(filename) {
  return {
    margin: [14, 18, 10, 8], // Top: 14mm, Left (punch): 18mm, Bottom: 10mm, Right: 8mm
    filename,
    image: { type: "jpeg", quality: 0.98 },
    html2canvas: {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: "#ffffff",
      scrollX: 0,
      scrollY: 0
    },
    jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
    pagebreak: { mode: ["css", "legacy"] }
  };
}

/**
 * Downloads the GST Report as an A4 PDF directly using html2pdf
 */
export async function downloadGstReportPdf(reportElement, filename = "GST_Filing_Report.pdf") {
  if (!reportElement) {
    throw new Error("Printable report element not found");
  }

  const opt = getPdfOptions(filename);
  await html2pdf().set(opt).from(reportElement).save();
  return true;
}

/**
 * Generates GST Report PDF and shares directly to WhatsApp or accountant
 */
export async function shareGstReportPdf(reportElement, reportData, company = {}, accountantPhone = "", periodLabel = "") {
  if (!reportElement) {
    throw new Error("Printable report element not found");
  }

  const cleanPeriod = (periodLabel || "Period").replace(/[^a-zA-Z0-9_-]/g, "_");
  const filename = `GST_Report_${cleanPeriod}.pdf`;
  const opt = getPdfOptions(filename);

  const pdfBlob = await html2pdf().set(opt).from(reportElement).outputPdf("blob");
  const file = new File([pdfBlob], filename, { type: "application/pdf" });
  const shareText = formatAccountantWhatsAppMessage(reportData, company, periodLabel);

  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({
        files: [file],
        title: `GST Filing Report - ${company.name || "Business"}`,
        text: shareText
      });
      return { sharedDirectly: true };
    } catch (shareErr) {
      if (shareErr.name === "AbortError") return { aborted: true };
    }
  }

  // Fallback: Download PDF and open WhatsApp Web / App
  const blobUrl = URL.createObjectURL(pdfBlob);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(blobUrl), 2000);

  const rawPhone = String(accountantPhone || "").replace(/\D/g, "");
  const targetPhone = rawPhone ? `91${rawPhone.slice(-10)}` : "";
  const fallbackMsg = `${shareText}\n\n📄 _(GST Report PDF has been downloaded to your device)_`;

  const waUrl = targetPhone
    ? `https://api.whatsapp.com/send?phone=${targetPhone}&text=${encodeURIComponent(fallbackMsg)}`
    : `https://api.whatsapp.com/send?text=${encodeURIComponent(fallbackMsg)}`;

  window.open(waUrl, "_blank");
  return { sharedDirectly: false, downloaded: true };
}
